import * as React from "react";
import { useCallback, useEffect } from "react";
import { InputChangeEvent } from "@progress/kendo-react-inputs/dist/npm/input/interfaces/InputChangeEvent";
import { DatePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import { TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import DeleteIcon from "@mui/icons-material/Delete";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "@mui/material";
import { InboundReturnTabControl } from "./LeftSide/InboundReturnTabControl";
import RightSide from "./RightSide/RightSide";
import { ProjectViewModel } from "../ViewModel/ProjectViewModel";
import { getProject } from "./GetProject";
import { EditProjectContext, EditProjectContextProps } from "./EditProjectContext";
import { RouteEditTab } from "./RouteEditTab";
import { SpdrClientViewModel } from "../ViewModel/SpdrClientViewModel";
import LoadingPanel from "../../Layout/LoadingPanel";
import { TopBaseData } from "./TopBaseData";
import { handleCardOnDragEnd } from "./handleCardOnDragEnd/handleCardOnDragEnd";
import { PdsrClientViewModel } from "../ViewModel/PdsrClientViewModel";
import PageHeader from "../../Layout/Header/PageHeader";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import SaveProject from "./SaveProject";
import { ProjectState } from "../ViewModel/ProjectState";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { useDeleteProject } from "./useDeleteProject";
import { gotoNextProjectState } from "./GotoNextProjectState";
import { TransitionNavigationParameter } from "./TransitionNavigationParameter";
import { IconWithRightMargin } from "../../Layout/Icons/IconWithRightMargin";
import StyledPageMenuIcon from "../../Layout/PageMenu/PageMenuIcon";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { isProjectReadOnly } from "./IsProjectReadOnly";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

const StyledFlexAlignEnd = styled.div`
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
`;

export default function EditProject() {
  const { projectId } = useParams();
  const [project, setProject] = React.useState<ProjectViewModel>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [routeEditTabSelected, setRouteEditTabSelected] = React.useState<RouteEditTab>(
    RouteEditTab.Ftl
  );
  const [selectedLtlSpdrOrPdsr, setSelectedLtlSpdrOrPdsr] = React.useState<
    SpdrClientViewModel | PdsrClientViewModel | undefined
  >();

  const [stopDataOfSpdrsOrPdsrs, setStopDataOfSpdrsOrPdsrs] = React.useState<Array<number>>([]);

  const handleUpdateProject = useCallback((updateProject: ProjectViewModel) => {
    setProject(updateProject);
  }, []);

  const deleteProject = useDeleteProject();

  useEffect(() => {
    async function requestData() {
      setIsLoading(true);
      try {
        await getProject({
          id: projectId,
          handleUpdateProject,
        });
      } catch (err: any) {
        toast.error(err.message);
      } finally {
        setIsLoading(false);
      }
    }

    requestData().catch(console.error);
  }, [projectId, handleUpdateProject]);

  const handleChanges = (event: InputChangeEvent | DatePickerChangeEvent) => {
    const { name, value } = event.target;

    const updateProject: ProjectViewModel = {
      ...project,
      [name as never]: value,
    };
    setProject(updateProject);
  };

  const handleTextAreaChanges = (event: TextAreaChangeEvent) => {
    const { value } = event;

    const { name } = event.target;

    const updateProject: ProjectViewModel = {
      ...project,
      [name as never]: value,
    };
    setProject(updateProject);
  };

  const contextValue = React.useMemo<EditProjectContextProps>(
    () => ({
      project,
      setProject: handleUpdateProject,
      reloadData: () => getProject({ id: projectId, handleUpdateProject }),
      routeEditTabSelected,
      setRouteEditTabSelected,
      stopDataOfSpdrsOrPdsrs,
      setStopDataOfSpdrsOrPdsrs,
      selectedLtlSpdrOrPdsr,
      setSelectedLtlSpdrOrPdsr,
    }),
    [
      project,
      handleUpdateProject,
      routeEditTabSelected,
      stopDataOfSpdrsOrPdsrs,
      projectId,
      setRouteEditTabSelected,
      selectedLtlSpdrOrPdsr,
    ]
  );

  const theme = useTheme();

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(`Do you really want to delete Project with Id ${projectId}?`);
    if (!isConfirmed) {
      return;
    }
    await deleteProject(projectId);
  };

  const proposeProjectWithConfirmation = async () => {
    const isConfirmed = await confirm(
      "Once you propose the project your LTL pipelines will be directly pending launch (impact on TT feed)! - Do you want to propose?"
    );
    if (!isConfirmed) {
      return;
    }
    await SaveProject({ project, showToast: false });
    await proposeProject();
  };

  const proposeProject = async () => {
    await gotoNextProjectState(TransitionNavigationParameter.Propose, project, setProject);
  };

  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);
  const pageHeader = (
    <PageHeader headerText="Edit Project">
      <PageHeaderIconButton
        onClick={() => onDeleteWithConfirmation()}
        color="secondary"
        disabled={isProjectReadOnly(project) || !isUserInAdminNetworkRole}
      >
        <DeleteIcon />
      </PageHeaderIconButton>
      {project.State === ProjectState.Proposed && (
        <PageHeaderButton
          sx={{ color: theme.palette.grey[100] }}
          onClick={() =>
            gotoNextProjectState(TransitionNavigationParameter.Sandbox, project, setProject)
          }
          variant="outlined"
          disabled={!isUserInAdminNetworkRole}
        >
          <IconWithRightMargin icon={faChevronLeft} />
          Back to Sandbox
        </PageHeaderButton>
      )}
      {project.State === ProjectState.Sandbox && (
        <PageHeaderButton
          sx={{ color: theme.palette.grey[100] }}
          onClick={() => proposeProjectWithConfirmation()}
          variant="outlined"
          disabled={!isUserInAdminNetworkRole}
        >
          <StyledPageMenuIcon icon={faChevronRight} />
          Propose
        </PageHeaderButton>
      )}
      <PageHeaderButton
        onClick={() => {
          SaveProject({ project }).then((savedProject: ProjectViewModel) => {
            setProject(savedProject);
          });
        }}
        disabled={
          project.State === ProjectState.Proposed ||
          project.State === ProjectState.Active ||
          !isUserInAdminNetworkRole
        }
      >
        Save
      </PageHeaderButton>
    </PageHeader>
  );
  if (isLoading) {
    return (
      <>
        {pageHeader}
        <LoadingPanel isLoading={isLoading} />
      </>
    );
  }

  return (
    <EditProjectContext.Provider value={contextValue}>
      {pageHeader}
      <TopBaseData
        handleChanges={handleChanges}
        handleTextAreaChanges={handleTextAreaChanges}
        isReadOnly={!isUserInAdminNetworkRole}
      />
      <DragDropContext
        onDragEnd={(result: DropResult) => handleCardOnDragEnd(result, contextValue)}
      >
        <StyledFlexAlignEnd>
          <InboundReturnTabControl isReadOnly={!isUserInAdminNetworkRole} />
          <RightSide isReadOnly={!isUserInAdminNetworkRole} />
        </StyledFlexAlignEnd>
      </DragDropContext>
    </EditProjectContext.Provider>
  );
}
